import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  LuArrowLeft,
  LuCheck,
  LuTriangleAlert,
  LuX,
  LuClock,
  LuDownload,
  LuFolderOpen,
} from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { recepcionesService } from '../../api/services/recepciones.service'
import {
  EstadoRecepcion,
  ResultadoValidacion,
  type DetalleRecepcionResponse,
  type DetalleReceptionValidationItem,
  type DisplayCardHighlight,
  type DisplayCardRow,
} from '../../api/models/recepcion.models'
import { parseUtcDate } from '../../lib/dates'

const MotionBox = motion.create(Box)

const ESTADO_LABELS: Record<EstadoRecepcion, { tid: string; color: string }> = {
  [EstadoRecepcion.Pendiente]: { tid: 'recepciones.status.pendiente', color: '#857b71' },
  [EstadoRecepcion.Aprobada]: { tid: 'recepciones.status.aprobada', color: '#2a8a87' },
  [EstadoRecepcion.Rechazada]: { tid: 'recepciones.status.rechazada', color: '#c8442f' },
}

// Per-result presentation (icon, accent colour, status pill background) — mirrors
// the mobile DataTriggers in ValidationDetailPage.xaml.
const RESULTADO_CONFIG: Record<
  ResultadoValidacion,
  { icon: typeof LuCheck; color: string; iconBg: string; statusColor: string; tid: string }
> = {
  [ResultadoValidacion.Pendiente]: {
    icon: LuClock,
    color: '#857b71',
    iconBg: '#e8dfd1',
    statusColor: '#857b71',
    tid: 'recepciones.resultado.pendiente',
  },
  [ResultadoValidacion.Conforme]: {
    icon: LuCheck,
    color: '#10B981',
    iconBg: '#D1FAE5',
    statusColor: '#10B981',
    tid: 'recepciones.resultado.conforme',
  },
  [ResultadoValidacion.Observado]: {
    icon: LuTriangleAlert,
    color: '#d49a2a',
    iconBg: '#f9eccf',
    statusColor: '#8a5f0b',
    tid: 'recepciones.resultado.observado',
  },
  [ResultadoValidacion.NoConforme]: {
    icon: LuX,
    color: '#c8442f',
    iconBg: '#fae1db',
    statusColor: '#9a3020',
    tid: 'recepciones.resultado.noConforme',
  },
}

// Variants used by DisplayCard rows/highlight to colour-code per-field outcomes.
const ROW_VARIANT_BG: Record<string, string> = {
  positive: '#ECFDF5',
  warning: '#fcf6e8',
  negative: '#fcefeb',
}
const ROW_VARIANT_TEXT: Record<string, string> = {
  positive: '#047857',
  warning: '#8a5f0b',
  negative: '#9a3020',
}
const HIGHLIGHT_VARIANT_BAR: Record<string, string> = {
  positive: '#10B981',
  warning: '#d49a2a',
  negative: '#c8442f',
  neutral: '#857b71',
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '-'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

export default function DetalleRecepcionPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const colors = themeColors
  const { t } = useT()

  const [detalle, setDetalle] = useState<DetalleRecepcionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!id) return
    const fetchDetalle = async () => {
      setLoading(true)
      setError(false)
      try {
        const data = await recepcionesService.getDetalle(id)
        setDetalle(data)
      } catch {
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    fetchDetalle()
  }, [id])

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Text tid="recepciones.detail.loadingMessage" color={colors.textMuted} fontSize="sm" />
      </VStack>
    )
  }

  if (error || !detalle) {
    return (
      <VStack gap={4} py={20}>
        <Text tid="recepciones.detail.notFound" color={colors.text} fontSize="lg" />
        <Box
          as="button"
          color={brand.green}
          fontSize="sm"
          cursor="pointer"
          _hover={{ textDecoration: 'underline' }}
          onClick={() => navigate('/app/recepciones')}
        >
          {t('recepciones.actions.volverRecepciones')}
        </Box>
      </VStack>
    )
  }

  const estadoInfo = ESTADO_LABELS[detalle.estado] ?? ESTADO_LABELS[EstadoRecepcion.Pendiente]

  // The two reception PDFs (acta + verification report) are served by [Authorize]
  // endpoints, so a plain <a href> would 401. Fetch with the Bearer token, build
  // a blob URL and open that in a new tab.
  const openAuthenticatedFile = async (url: string) => {
    const token = localStorage.getItem('auth_token')
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    if (!res.ok) return
    const blob = await res.blob()
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer')
  }
  const apiBase = import.meta.env.VITE_API_BASE_URL
  const actaUrl = detalle.tieneActa ? `${apiBase}/recepciones/${detalle.id}/acta` : null
  // The verification report belongs to a validation run captured on mobile;
  // we only expose a download here if a report already exists (we don't
  // synthesize new runs from the web — that would misrepresent when validation
  // results were taken).
  const verificationReportUrl = detalle.tieneVerificationReport
    ? `${apiBase}/recepciones/${detalle.id}/verification-report`
    : null

  const detalleFields = [
    { label: t('recepciones.detail.fields.proveedor'), value: detalle.supplierName ?? '-', highlight: true },
    { label: t('recepciones.detail.fields.ruc'), value: detalle.supplierRuc ?? '-' },
    { label: t('recepciones.detail.fields.mineral'), value: detalle.mineralTypeName ?? '-' },
    { label: t('recepciones.detail.fields.fecha'), value: formatDateTime(detalle.receivedAt) },
    { label: t('recepciones.detail.fields.observaciones'), value: detalle.observations ?? '-' },
  ]

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <Flex alignItems="center" gap={3}>
        <Box
          as="button"
          p={2}
          borderRadius="xl"
          cursor="pointer"
          color={colors.textMuted}
          _hover={{ bg: colors.bgHover, color: colors.text }}
          transition="all 0.2s"
          onClick={() => navigate('/app/recepciones')}
        >
          <LuArrowLeft size={20} />
        </Box>
        <Flex alignItems="center" gap={2}>
          <Text
            tid="recepciones.breadcrumb"
            fontSize="sm"
            color={colors.textMuted}
            cursor="pointer"
            _hover={{ color: brand.green }}
            onClick={() => navigate('/app/recepciones')}
          />
          <Text fontSize="sm" color={colors.textDimmed}>
            /
          </Text>
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            {detalle.batchCode}
          </Text>
        </Flex>
      </Flex>

      {/* Header card */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        p={6}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        boxShadow="none"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
          <Text fontSize="lg" fontWeight="bold" color={colors.text}>
            {detalle.batchCode}
          </Text>
          <Flex gap={2} alignItems="center">
            <Box
              px={2.5}
              py={1}
              borderRadius="full"
              fontSize="10px"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="0.06em"
              bg={`${estadoInfo.color}33`}
              color={estadoInfo.color}
            >
              {t(estadoInfo.tid)}
            </Box>
            {verificationReportUrl && (
              <Box
                as="button"
                {...{ type: 'button' }}
                onClick={() => openAuthenticatedFile(verificationReportUrl)}
                display="flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                bg={colors.bgHover}
                color={colors.text}
                border="1px solid"
                borderColor={colors.border}
                borderRadius="lg"
                fontSize="xs"
                fontWeight="600"
                cursor="pointer"
                _hover={{ bg: colors.bg }}
                transition="all 0.2s"
              >
                <LuDownload size={14} />
                {t('recepciones.actions.downloadVerificationReport')}
              </Box>
            )}
            {actaUrl && (
              <Box
                as="button"
                {...{ type: 'button' }}
                onClick={() => openAuthenticatedFile(actaUrl)}
                display="flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                bg={brand.primary}
                color="white"
                borderRadius="lg"
                fontSize="xs"
                fontWeight="600"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                transition="all 0.2s"
              >
                <LuDownload size={14} />
                {t('recepciones.actions.downloadActa')}
              </Box>
            )}
            {/* El expediente del lote nace al cerrar la recepción (acta emitida). */}
            {actaUrl && (
              <Box
                as="button"
                {...{ type: 'button' }}
                onClick={() =>
                  navigate(`/app/identidad/expedientes/${encodeURIComponent(detalle.batchCode)}/electronico`)
                }
                display="flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                bg={colors.bgHover}
                color={colors.text}
                border="1px solid"
                borderColor={colors.border}
                borderRadius="lg"
                fontSize="xs"
                fontWeight="600"
                cursor="pointer"
                _hover={{ bg: colors.bg }}
                transition="all 0.2s"
              >
                <LuFolderOpen size={14} />
                {t('recepciones.actions.viewBatchExpediente')}
              </Box>
            )}
          </Flex>
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
          gap={4}
        >
          {detalleFields.map((item) => (
            <Box key={item.label}>
              <Text
                fontSize="10px"
                textTransform="uppercase"
                letterSpacing="0.15em"
                fontWeight="bold"
                color={colors.textMuted}
              >
                {item.label}
              </Text>
              {'highlight' in item && item.highlight ? (
                <Box
                  display="inline-block"
                  bg={`${brand.green}12`}
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  mt={0.5}
                >
                  <Text fontSize="sm" fontWeight="700" color={brand.green}>
                    {item.value}
                  </Text>
                </Box>
              ) : (
                <Text fontSize="sm" fontWeight="500" color={colors.text} mt={0.5}>
                  {item.value}
                </Text>
              )}
            </Box>
          ))}
        </Grid>
      </MotionBox>

      {/* Validations table */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        boxShadow="none"
      >
        <Box p={5} borderBottom="1px solid" borderColor={colors.border}>
          <Text
            tid="recepciones.detail.resultsTitle"
            fontSize="md"
            fontWeight="bold"
            color={colors.text}
          />
        </Box>

        <VStack gap={3} alignItems="stretch" p={4}>
          {(detalle.validations ?? []).map((val) => (
            <ValidationDetailCard key={val.id} validation={val} colors={colors} />
          ))}
        </VStack>
      </MotionBox>
    </VStack>
  )
}

// ── Validation Detail Card ──
// Mirrors the mobile ValidationDetailPage.xaml layout: status icon + title +
// status pill, optional highlight card, key/value rows (variant-tinted), and
// the operator observation block. The web page consumes the same DisplayCardDto
// the mobile app does.

interface ValidationDetailCardProps {
  validation: DetalleReceptionValidationItem
  colors: typeof themeColors
}

function ValidationDetailCard({ validation, colors }: ValidationDetailCardProps) {
  const { t } = useT()
  const resultInfo = RESULTADO_CONFIG[validation.result] ?? RESULTADO_CONFIG[ResultadoValidacion.Pendiente]
  const Icon = resultInfo.icon

  const card = validation.displayCard
  const title = card?.title ?? validation.name
  const statusLabel = card?.statusLabel ?? t(resultInfo.tid).toUpperCase()
  const observationDetail = validation.operatorObservation ?? validation.observation ?? null

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={colors.border}
      borderRadius="xl"
      p={5}
      boxShadow="0 1px 2px rgba(15,62,42,0.04)"
    >
      <VStack gap={3.5} alignItems="stretch">
        {/* Header: status icon + title + status label */}
        <Flex gap={3} alignItems="flex-start">
          <Flex
            width="32px"
            height="32px"
            borderRadius="full"
            bg={resultInfo.iconBg}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon size={14} color={resultInfo.color} />
          </Flex>
          <Box flex={1} minW={0}>
            <Text fontSize="md" fontWeight="700" color="#0F3E2A" lineHeight="1.3">
              {title}
            </Text>
            <Text
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.08em"
              color={resultInfo.statusColor}
              mt={0.5}
            >
              {statusLabel}
            </Text>
          </Box>
        </Flex>

        {/* Highlight card on the right with coloured left bar */}
        {card?.highlight && <HighlightCard highlight={card.highlight} />}

        {/* Key/value rows, variant-tinted when relevant */}
        {card?.rows && card.rows.length > 0 && (
          <VStack gap={1.5} alignItems="stretch">
            {card.rows.map((row, idx) => (
              <RowItem key={`${row.label}-${idx}`} row={row} />
            ))}
          </VStack>
        )}

        {/* Free-text observation (operator override or validator output) */}
        {observationDetail && (
          <Box bg="#F3F4F5" borderRadius="lg" p={3}>
            <Text
              fontSize="10px"
              fontWeight="700"
              letterSpacing="0.1em"
              color="#857b71"
              mb={1}
            >
              OBSERVACIÓN
            </Text>
            <Text fontSize="sm" color="#0F3E2A" lineHeight="1.5">
              {observationDetail}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  )
}

function HighlightCard({ highlight }: { highlight: DisplayCardHighlight }) {
  const variant = highlight.variant ?? 'neutral'
  const barColor = HIGHLIGHT_VARIANT_BAR[variant] ?? HIGHLIGHT_VARIANT_BAR.neutral

  return (
    <Flex justifyContent="flex-end">
      <Flex
        bg="#F3F4F5"
        borderRadius="md"
        overflow="hidden"
        minWidth="180px"
      >
        <Box width="4px" bg={barColor} flexShrink={0} />
        <Box px={4} py={2.5}>
          <Text
            fontSize="10px"
            fontWeight="700"
            letterSpacing="0.12em"
            color="#857b71"
          >
            {highlight.label}
          </Text>
          <Flex alignItems="baseline" gap={1.5}>
            <Text fontSize="xl" fontWeight="800" color="#0F3E2A" lineHeight="1.1">
              {highlight.value}
            </Text>
            {highlight.unit && (
              <Text fontSize="xs" color="#857b71">
                {highlight.unit}
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  )
}

function RowItem({ row }: { row: DisplayCardRow }) {
  const variant = row.variant ?? null
  const bg = variant ? ROW_VARIANT_BG[variant] : 'transparent'
  const valueColor = variant ? ROW_VARIANT_TEXT[variant] : '#0F3E2A'

  return (
    <Flex
      bg={bg}
      borderRadius="md"
      px={3}
      py={2}
      gap={3}
      alignItems="center"
      justifyContent="space-between"
    >
      <Text
        fontSize="11px"
        fontWeight="700"
        letterSpacing="0.06em"
        color="#857b71"
        flexShrink={0}
      >
        {row.label}
      </Text>
      <Text
        fontSize="13px"
        fontWeight="700"
        color={valueColor}
        textAlign="right"
        wordBreak="break-word"
      >
        {row.value}
      </Text>
    </Flex>
  )
}
